/**
 * Pool Dashboard (rev 16, 23/7 principal request — a full trim of what used to be here).
 *
 * Two things live on this screen now:
 *
 *  1. **License overview**, new — the estate at a glance: total pool accounts / seats / assigned /
 *     unassigned / utilization, broken down by account type AND as a grand total, so the answer to
 *     "how much license do we own, how much is used, how much is left" is the first thing on the
 *     page. Pure client-side aggregation over GET /pool/accounts (poolAccountStats.ts) — the same
 *     numbers Pool Accounts already shows per row, just summed; no new backend endpoint needed. An
 *     account whose Account type/Seats was never filled in (a standalone account added via
 *     credential, before rev 16's manual-entry fields existed) buckets into "Not set" and drags its
 *     seats out of the Seats/Unassigned/Utilization totals — visible pressure to go fill it in on
 *     the Settings screen, not a silently wrong number.
 *
 *  2. The two mismatch cards (design §10) — the module's original headline value, kept, RENAMED for
 *     clarity (principal feedback 23/7: the old "Zoom has it · we don't" / "We have it · Zoom
 *     doesn't" pronoun-swap titles read as a riddle at a glance). Card 1 is a licensed Zoom seat
 *     with no open service period (a possible unbilled or free license — filtered server-side to
 *     exclude owners/admins so a pool's own 42 owners never show up here); card 2 is an open period
 *     whose customer currently has no Licensed membership (paid, but can't use it — this only ever
 *     fills because the sweep NEVER auto-closes a period on its own observation). Both link a row
 *     into Pool Users filtered by that email.
 *
 * EVERYTHING ELSE that used to be on this screen — the two-clocks-conflict card, the renew-stalled
 * card, the 7-day sweep-observed tally, and the tripwire queue — is REMOVED FROM THIS PAGE ONLY
 * (principal instruction 23/7: "Dashboard เดิม เอาออกทั้งหมดเลย ยกเว้น [the two cards]"). The
 * backend (`GET /pool/dashboard`'s card3/4/5/tripwire_queue, service.dashboard_cards) is UNTOUCHED
 * — the tripwire queue in particular backs a real fail-closed alerting path in expiry.py
 * (should_alert/send_alert), not just this display, so cutting the read here changes nothing about
 * whether those alerts still fire. If the backend fields should also go, that's a separate call.
 */

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  EmptyState,
  ErrorState,
  SkeletonRows,
  StatusPill,
  Table,
  type Column,
} from "@/components/ui";
import { FreshnessBadge } from "@/components/portal/FreshnessBadge";
import { FeedNote, KpiTile, Section } from "@/features/customer/display";
import { PoolErrorState } from "@/features/pool/PoolErrorState";
import {
  UNKNOWN_TIER,
  aggregatePoolStats,
  bucketByTier,
  tierLabel,
} from "@/features/pool/poolAccountStats";
import {
  usePoolAccess,
  usePoolAccounts,
  usePoolDashboard,
  usePoolStatus,
} from "@/features/pool/hooks";
import { formatInt } from "@/lib/format";
import type { PoolAccount, PoolDashboardRow } from "@/api/types";

/** ปิดการ์ด mismatch สองใบชั่วคราว (คำสั่งคุณใหม่ 24/7/2026: "เอาออกก่อน แต่ไม่ลบทิ้ง เผื่อวันหนึ่ง
 *  ต้องการค่อยให้แสดงขึ้นมา") — flip เป็น true การ์ดทั้งสอง + การ fetch ของมันกลับมาทั้งชุด.
 *  Backend (GET /pool/dashboard) ไม่ถูกแตะ เหมือนแนวเดิมของ rev 16 (ตัดจากจอ ไม่ตัดจากระบบ). */
const SHOW_MISMATCH_CARDS: boolean = false;

function DashboardCard({
  title,
  subtitle,
  count,
  rows,
  emptyBody,
  tone,
}: {
  title: string;
  subtitle: string;
  count: number;
  rows: PoolDashboardRow[];
  emptyBody: string;
  tone: "danger" | "warn";
}) {
  const { t } = useTranslation();

  const columns: Column<PoolDashboardRow>[] = [
    {
      key: "email",
      header: t("pool.dashboard.colEmail"),
      sortValue: (r) => r.email,
      cell: (r) => r.email,
    },
    {
      key: "account",
      header: t("pool.dashboard.colAccount"),
      sortValue: (r) => r.account_name ?? "",
      cell: (r) => r.account_name ?? r.account_number ?? "—",
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <Link
          to={`/pool/users?email=${encodeURIComponent(r.email)}`}
          className="text-(length:--text-sm) font-medium text-(--brand-strong) hover:underline"
        >
          {t("pool.dashboard.viewInUsers")}
        </Link>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-3 rounded-(--radius) border border-(--border) bg-(--surface) p-4 shadow-(--shadow-sm)">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-(length:--text-lg) font-semibold text-(--text-strong)">{title}</h2>
          <p className="text-(length:--text-xs) text-(--text-muted)">{subtitle}</p>
        </div>
        <StatusPill tone={tone}>{formatInt(count)}</StatusPill>
      </div>
      <Table
        columns={columns}
        rows={rows}
        rowKey={(r) => r.email}
        caption={title}
        empty={<EmptyState title={t("pool.dashboard.emptyTitle")} body={emptyBody} />}
      />
    </section>
  );
}

/** One row of the by-account-type breakdown table — the same Seats/Assigned/Unassigned/Utilization
 *  shape PoolAccountsPage already renders per account, here summed per tier (poolAccountStats.ts). */
interface TierRow {
  tier: string;
  accounts: number;
  seats: number | null;
  assigned: number;
  unassigned: number | null;
  utilizationPct: number | null;
}

function OverviewSection({ accounts }: { accounts: PoolAccount[] }) {
  const { t } = useTranslation();

  const total = aggregatePoolStats(accounts);
  const buckets = bucketByTier(accounts);
  const tierRows: TierRow[] = buckets.map(({ tier, rows }) => ({
    tier,
    ...aggregatePoolStats(rows),
  }));

  // Adversarial review (23/7): Total Unassigned/Utilization subtract Seats (known-accounts only)
  // from Assigned (EVERY account, by design — see this file's own banner) — an account with real
  // assigned members but no recorded Seats yet makes those two totals a blended figure with no
  // visible signal. Surface it explicitly, but ONLY when it actually affects the numbers (some of
  // those "Not set" accounts have real assigned members) — an unfilled account with zero members
  // yet doesn't distort anything, so no note is warranted for it (DESIGN.md: hide what's not real).
  const unknownAssigned = aggregatePoolStats(
    buckets.find((b) => b.tier === UNKNOWN_TIER)?.rows ?? [],
  ).assigned;

  const columns: Column<TierRow>[] = [
    {
      key: "tier",
      header: t("pool.dashboard.overview.colAccountType"),
      cell: (r) => tierLabel(t, r.tier),
    },
    {
      key: "accounts",
      header: t("pool.dashboard.overview.colAccounts"),
      numeric: true,
      cell: (r) => formatInt(r.accounts),
    },
    {
      key: "seats",
      header: t("pool.dashboard.overview.colSeats"),
      numeric: true,
      cell: (r) => (r.seats != null ? formatInt(r.seats) : "—"),
    },
    {
      key: "assigned",
      header: t("pool.dashboard.overview.colAssigned"),
      numeric: true,
      cell: (r) => formatInt(r.assigned),
    },
    {
      key: "unassigned",
      header: t("pool.dashboard.overview.colUnassigned"),
      numeric: true,
      cell: (r) =>
        r.unassigned == null ? (
          "—"
        ) : (
          <span className={r.unassigned < 0 ? "font-semibold text-(--warn)" : undefined}>
            {formatInt(r.unassigned)}
          </span>
        ),
    },
    {
      key: "utilization",
      header: t("pool.dashboard.overview.colUtilization"),
      numeric: true,
      cell: (r) =>
        r.utilizationPct == null ? (
          "—"
        ) : (
          <span className={r.utilizationPct > 100 ? "font-semibold text-(--warn)" : undefined}>
            {`${r.utilizationPct}%`}
          </span>
        ),
    },
  ];

  return (
    <Section title={t("pool.dashboard.overview.title")}>
      <p className="-mt-1 text-(length:--text-sm) text-(--text-muted)">
        {t("pool.dashboard.overview.subtitle")}
      </p>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
        <KpiTile
          label={t("pool.dashboard.overview.tileAccounts")}
          value={formatInt(total.accounts)}
        />
        <KpiTile
          label={t("pool.dashboard.overview.tileSeats")}
          value={total.seats != null ? formatInt(total.seats) : "—"}
        />
        <KpiTile
          label={t("pool.dashboard.overview.tileAssigned")}
          value={formatInt(total.assigned)}
        />
        <KpiTile
          label={t("pool.dashboard.overview.tileUnassigned")}
          value={
            total.unassigned == null ? (
              "—"
            ) : (
              <span className={total.unassigned < 0 ? "text-(--warn)" : undefined}>
                {formatInt(total.unassigned)}
              </span>
            )
          }
        />
        <KpiTile
          label={t("pool.dashboard.overview.tileUtilization")}
          value={
            total.utilizationPct == null ? (
              "—"
            ) : (
              <span className={total.utilizationPct > 100 ? "text-(--warn)" : undefined}>
                {`${total.utilizationPct}%`}
              </span>
            )
          }
        />
      </div>
      {unknownAssigned > 0 && (
        <FeedNote>{t("pool.dashboard.overview.blendedNote", { count: unknownAssigned })}</FeedNote>
      )}
      {accounts.length === 0 ? (
        <EmptyState
          title={t("pool.dashboard.overview.emptyTitle")}
          body={t("pool.dashboard.overview.emptyBody")}
        />
      ) : (
        <Table
          columns={columns}
          rows={tierRows}
          rowKey={(r) => r.tier}
          caption={t("pool.dashboard.overview.breakdownTitle")}
          paginated={false}
        />
      )}
    </Section>
  );
}

/**
 * 30/7 (คุณใหม่: "role Viewer ไม่ให้เห็น dashboard") — `_manage` only, gated on `usePoolAccess()` BEFORE
 * any dashboard/accounts request fires, exactly like PoolSystemPage. A pool-viewer (or a caller with no
 * pool role at all) gets the app's normal RBAC-forbidden copy, NOT <PoolErrorState/> — that copy means
 * "not a pool operator", a different story from "you're an operator, just not manage-capable". The tab
 * is hidden and /pool sends a viewer to Accounts, so reaching this is deliberate URL entry; the server
 * 403s GET /pool/dashboard on its own regardless (router.py) — this gate only makes the refusal legible
 * instead of a half-rendered screen with an error card in it.
 */
export function PoolDashboardPage() {
  const { t } = useTranslation();
  const accessQ = usePoolAccess();

  if (accessQ.isPending) return <SkeletonRows rows={3} />;
  if (accessQ.isError) {
    return <ErrorState error={accessQ.error} onRetry={() => void accessQ.refetch()} />;
  }

  const role = accessQ.data.role;
  if (role !== "owner" && role !== "admin") {
    return <ErrorState title={t("state.forbiddenTitle")} body={t("state.forbiddenBody")} />;
  }

  return <PoolDashboardContent />;
}

function PoolDashboardContent() {
  const { t } = useTranslation();
  const dashboardQ = usePoolDashboard(SHOW_MISMATCH_CARDS);
  const statusQ = usePoolStatus();
  const accountsQ = usePoolAccounts();

  return (
    <section aria-labelledby="pool-dashboard-title" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <h1
            id="pool-dashboard-title"
            className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
          >
            {t("pool.dashboard.title")}
          </h1>
          {!statusQ.isPending && !statusQ.isError && (
            <FreshnessBadge
              kind="mirror"
              timestamp={statusQ.data.last_complete?.finished_at ?? null}
            />
          )}
        </div>
        <p className="text-(length:--text-sm) text-(--text-muted)">
          {t("pool.dashboard.subtitle")}
        </p>
      </header>

      {accountsQ.isPending ? (
        <SkeletonRows rows={3} />
      ) : accountsQ.isError ? (
        <PoolErrorState error={accountsQ.error} onRetry={() => void accountsQ.refetch()} />
      ) : (
        <OverviewSection accounts={accountsQ.data.accounts} />
      )}

      {SHOW_MISMATCH_CARDS &&
        (dashboardQ.isPending ? (
          <SkeletonRows rows={3} />
        ) : dashboardQ.isError ? (
          <PoolErrorState error={dashboardQ.error} onRetry={() => void dashboardQ.refetch()} />
        ) : dashboardQ.data ? (
          <div className="grid gap-5 lg:grid-cols-2">
            <DashboardCard
              title={t("pool.dashboard.card1Title")}
              subtitle={t("pool.dashboard.card1Subtitle")}
              count={dashboardQ.data.card1_license_without_period.count}
              rows={dashboardQ.data.card1_license_without_period.rows}
              emptyBody={t("pool.dashboard.card1EmptyBody")}
              tone="danger"
            />
            <DashboardCard
              title={t("pool.dashboard.card2Title")}
              subtitle={t("pool.dashboard.card2Subtitle")}
              count={dashboardQ.data.card2_period_without_license.count}
              rows={dashboardQ.data.card2_period_without_license.rows}
              emptyBody={t("pool.dashboard.card2EmptyBody")}
              tone="warn"
            />
          </div>
        ) : null)}
    </section>
  );
}
